/**
 * A plain text box for the short things you write about yourself: a status, a
 * party title, a bio.
 *
 * It used to carry the emoji picker in the corner of the field. Nobody wanted
 * it there - the one place a picker earns its keep is the chat composer, which
 * has its own - and a button sitting inside every single input was noise on
 * every form in the app. Custom emoji still work by typing `:name:`; the
 * picker is gone.
 */

import { useRef } from 'react';
import { cn } from '@/lib/cn';

type Field = HTMLInputElement | HTMLTextAreaElement;

export function EmojiField({ value, onChange, textarea, className, ...rest }: {
  value: string;
  onChange: (next: string) => void;
  /** Multi-line, for a description or a bio. */
  textarea?: boolean;
  className?: string;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  disabled?: boolean;
  spellCheck?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<Field>) => void;
}) {
  const field = useRef<Field>(null);

  const shared = {
    ...rest,
    value,
    onChange: (e: React.ChangeEvent<Field>) => onChange(e.target.value),
    className: cn(
      'w-full rounded-[var(--radius-s)] border border-line bg-surface-1 text-[13.5px] outline-none',
      'transition-colors placeholder:text-ghost focus:border-accent',
      textarea ? 'min-h-[86px] resize-none p-3' : 'h-[var(--h)] px-3',
      className
    )
  };

  return textarea
    ? <textarea ref={field as React.RefObject<HTMLTextAreaElement>} {...shared} />
    : <input ref={field as React.RefObject<HTMLInputElement>} {...shared} />;
}
